import React, { useEffect, useRef } from 'react'

import { useConfigurationStore, useFreeSpinsBonusesStore } from '@stores'

import spritesheetSrc from '@assets/bonus-system/gift-animation.png'
import animatedSpriteConfig from '@assets/bonus-system/gift-animation.json'
import { formatMoney } from '@utils/money'
import { AnimatedSprite, useShrinkTextToFit } from '@amusnet/react-ui'
import { useTranslation } from '@amusnet/translations/react'

const FSEndBonus: React.FC = () => {
    const totalWin = useFreeSpinsBonusesStore((state) => state.totalWin)
    const setTotalWin = useFreeSpinsBonusesStore((state) => state.setTotalWin)
    const hasActiveBonusesLeft = useFreeSpinsBonusesStore((state) => state.hasActiveBonusesLeft)
    const setShowRemainingSpinsBanner = useFreeSpinsBonusesStore((state) => state.setShowRemainingSpinsBanner)

    const { t } = useTranslation()
    const currency = useConfigurationStore((state) => state.currency)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)

    const formattedTotalWin = formatMoney(Number(totalWin), currencyHideCents)
    const totalWinAmountRef = useRef<HTMLParagraphElement>(null)

    const text = hasActiveBonusesLeft
        ? t('gameui.bonusSystem.stillHaveActiveBonuses')
        : t('gameui.bonusSystem.playingWithRealMoney')
    const bottomTextRef = useRef<HTMLParagraphElement>(null)

    if (totalWin === '0') {
        return (
            <div className="flex flex-col items-center justify-between">
                <div className="flex flex-col w-full bg-[#19191C]">
                    <AnimatedSprite
                        spritesheetSrc={spritesheetSrc}
                        config={animatedSpriteConfig}
                        duration={1.5}
                        className="m-auto"
                    />
                    <div
                        id="end-bonus-text"
                        className="flex flex-col items-center w-full h-[273px] bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)] text-center"
                    >
                        <p className="text-4xl font-bold text-white">
                            {t('gameui.bonusSystem.youDidNotScoreAWin') ?? "You didn't score a win."}
                        </p>
                        <p className="text-4xl font-black text-yellow-400 leading-tight">
                            {t('gameui.bonusSystem.betterLuckNextTime') ?? 'BETTER LUCK NEXT TIME!'}
                        </p>
                        <p className="text-4xl font-bold text-white pt-14">{text}</p>
                    </div>
                </div>
            </div>
        )
    }

    useEffect(() => {
        return () => {
            setTotalWin('0')
            setShowRemainingSpinsBanner(false)
        }
    }, [])

    useShrinkTextToFit({
        refresh: [formattedTotalWin],
        designFontSize: 128,
        bounds: { width: 590 },
        textRef: totalWinAmountRef,
    })

    useShrinkTextToFit({
        refresh: [text],
        designFontSize: 36,
        bounds: { width: 720 },
        textRef: bottomTextRef,
    })

    return (
        <div className="flex flex-col items-center justify-between">
            <div className="flex flex-col w-full bg-[#19191C]">
                <AnimatedSprite
                    spritesheetSrc={spritesheetSrc}
                    config={animatedSpriteConfig}
                    duration={1.5}
                    className="m-auto"
                />
                <div
                    id="end-bonus-text"
                    className="flex flex-col items-center w-full h-[273px] bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]"
                >
                    <p className="text-4xl font-bold text-white">{t('gameui.bonusSystem.hasEnded')}</p>
                    <p className="text-4xl font-black text-yellow-400 leading-tight">
                        {t('gameui.bonusSystem.youHaveWon')}
                    </p>

                    <div className="flex flex-col items-center leading-tight">
                        <p className="text-9xl font-black text-yellow-400" ref={totalWinAmountRef}>
                            {formattedTotalWin}
                            <span className="text-5xl font-black text-yellow-400 pl-2">{currency}</span>
                        </p>
                    </div>

                    <p className="text-4xl font-bold text-white" ref={bottomTextRef}>
                        {text}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default FSEndBonus
